import { BASE_CURRENCY, formatMoney } from "@/lib/money";

// Plan catalog for the pricing surfaces (landing page, Billing, Signup).
//
// The NUMBERS are not written here. They come from GET /billing/plans, which
// serves the same catalog every limit check reads, so a card can no longer
// advertise a limit different from the one enforced — the Growth card used to
// promise "2,500 test runs / month" beside a usage meter counting against 5,000,
// and the Free Trial card promised 10 runs against an enforced 50.
//
// The list below is a FALLBACK for when that call fails (or for the signed-out
// landing page on a cold backend). It mirrors the backend defaults; if an admin
// has edited the catalog, the fetched values win.

// Matches the backend's UNLIMITED_AT — at or above this a limit is "Unlimited".
export const UNLIMITED = 999999;

export function isUnlimited(n) {
  return Number(n) >= UNLIMITED;
}

export function formatLimit(n) {
  return isUnlimited(n) ? "Unlimited" : Number(n || 0).toLocaleString("en-IN");
}

// Formats in the plan's OWN currency. A per-country override may charge in USD,
// JPY or KWD, so the hardcoded rupee sign this replaces rendered a $49 plan as
// "₹49" — the same class of error as charging it in the wrong currency, one
// layer up. `currency` defaults to the base currency, which is what every plan
// without an override is priced in.
export function formatPrice(price, currency = BASE_CURRENCY) {
  // A null price is the Custom tier — quoted, not listed.
  if (price === null || price === undefined) return "Custom";
  return formatMoney(Number(price) || 0, currency);
}

/** Can a coupon be applied to this plan?
 *
 * MIRRORS backend/api/routers/admin_coupons.py `_is_coupon_eligible`, which is
 * the control — this copy only decides what the picker OFFERS.
 *
 * The rule is read off the plan's price, never off its id, so it keeps working
 * for plans an administrator creates later:
 *
 *   price_inr == null  → "Custom / contact us". create-order refuses it
 *                        ("no self-serve price — contact sales"), so a coupon on
 *                        it could never be redeemed.
 *   price_inr == 0     → free. That is the Free Trial, and any other zero-priced
 *                        or trial plan; create-order refuses it too ("this plan
 *                        is free"). There is no amount to discount.
 *
 * `price_inr` and not `effectiveMonthly`: the latter coerces a null Custom price
 * to 0, which would make "contact sales" indistinguishable from "free" — both
 * ineligible here, but for reasons a future reader needs to keep apart.
 */
export function isCouponEligiblePlan(plan) {
  const price = plan?.price_inr;
  return price !== null && price !== undefined && Number(price) > 0;
}

// Copy with no enforced counterpart. Kept apart from the limits so a perk is
// never rendered as though the backend polices it.
const PERKS = {
  free_trial: [
    "Manual and AI-powered tests",
    "Bug reports with screenshots",
    "Full run history",
    "XLSX & PDF reports",
  ],
  starter: ["AI-powered bug detection", "Email support", "Basic analytics"],
  growth: [
    "Priority support",
    "Advanced analytics & trends",
    "Custom integrations",
    "Team collaboration",
  ],
  custom: [
    "Dedicated account manager",
    "On-premise deployment",
    "SSO & audit logging",
    "Custom SLA",
  ],
};

// The behaviour flags travel with the fallback too. Signup's picker selects on
// `is_trial || is_signup_pack`, so a fallback without them renders an empty
// picker — i.e. a permanent spinner — exactly when the API is unreachable and the
// fallback is supposed to be saving the page.
const FALLBACK = [
  {
    id: "free_trial", name: "Free Trial", label: "FREE", price_inr: 0, price_inr_yearly: 0, period: null,
    is_trial: true, is_signup_pack: false,
    trial_days: 14, blurb: "Try the full engine on us. No credit card needed.",
    cta: "Get started", featured: false, active: true,
    limits: { max_runs_per_month: 50, max_projects: 1, max_testers: 2 },
  },
  {
    id: "starter", name: "Starter Plan", label: "STARTER", price_inr: 3999, price_inr_yearly: 38390, period: "/month",
    is_trial: false, is_signup_pack: true,
    trial_days: 0, blurb: "Perfect for small teams getting started with automated QA.",
    cta: "Subscribe", featured: false, active: true,
    limits: { max_runs_per_month: 500, max_projects: 5, max_testers: 5 },
  },
  {
    id: "growth", name: "Growth Plan", label: "GROWTH", price_inr: 9999, price_inr_yearly: 95990, period: "/month",
    is_trial: false, is_signup_pack: true,
    trial_days: 0, blurb: "For growing teams that need more runs and deeper insights.",
    cta: "Subscribe", featured: true, active: true,
    limits: { max_runs_per_month: 5000, max_projects: 25, max_testers: 20 },
  },
  {
    id: "custom", name: "Custom Plan", label: "ENTERPRISE", price_inr: null, price_inr_yearly: null, period: null,
    is_trial: false, is_signup_pack: false,
    trial_days: 0, blurb: "Unlimited runs, dedicated support, and on-premise options.",
    cta: "Contact us", featured: false, active: true,
    limits: { max_runs_per_month: UNLIMITED, max_projects: UNLIMITED, max_testers: UNLIMITED },
  },
];

// The three quotas the backend actually enforces, in the order a card lists
// them. `key` is the field in the plan's `limits`; `usageKey` is the matching
// counter on GET /billing/usage, so a card and the usage bars stay in step.
export const LIMIT_ROWS = [
  { key: "max_runs_per_month", usageKey: "runs", label: "Test runs", suffix: "/ month" },
  { key: "max_projects", usageKey: "projects", label: "Projects", suffix: "" },
  { key: "max_testers", usageKey: "testers", label: "Tester seats", suffix: "" },
];

// Turn one API (or fallback) plan into what the cards render.
export function normalizePlan(plan) {
  const limits = plan.limits || {};
  const perks = plan.perks?.length ? plan.perks : PERKS[plan.id] || [];
  const limitLines = LIMIT_ROWS.map((row) => ({
    key: row.key,
    label: row.label,
    value: `${formatLimit(limits[row.key])}${row.suffix ? ` ${row.suffix}` : ""}`,
  }));
  if (plan.trial_days > 0) {
    limitLines.push({ key: "trial_days", label: "Free trial", value: `${plan.trial_days} days` });
  }
  // Not sourced from `plan.limits` — no plan meters this, it's unlimited on
  // every one of them (#83). A static line rather than a LIMIT_ROWS entry so
  // it never tries to read a `max_dev_pm`-style key that doesn't exist.
  limitLines.push({ key: "dev_pm_seats", label: "Developer & PM seats", value: "Unlimited" });
  // Whole-rupee-per-month equivalent when billed yearly, purely for the
  // savings badge (#89/#90) — never used for anything charged; the actual
  // yearly total is always price_inr_yearly as-is, not this divided back down.
  // What this viewer is actually charged. The API sends `price`/`price_yearly`/
  // `currency` already resolved for their country; `price_inr`/`price_inr_yearly`
  // remain the INR LIST price and are the fallback for an older payload (and for
  // the bundled offline catalog). Never read the `_inr` fields for display — that
  // is how a localized plan came to be shown at its rupee list price.
  const currency = plan.currency || BASE_CURRENCY;
  const effectiveMonthly = plan.price != null ? Number(plan.price) : Number(plan.price_inr || 0);
  const effectiveYearly =
    plan.price_yearly != null ? Number(plan.price_yearly) : Number(plan.price_inr_yearly || 0);

  // Per-month equivalent when billed yearly, purely for the savings badge
  // (#89/#90) — never used for anything charged; the actual yearly total is
  // always the yearly price as-is, not this divided back down. Computed from the
  // EFFECTIVE prices so the badge matches the numbers beside it.
  const yearlyMonthlyEquivalent =
    effectiveYearly ? Math.round(effectiveYearly / 12) : null;
  const yearlySavingsPercent =
    effectiveMonthly && effectiveYearly
      ? Math.round((1 - effectiveYearly / (effectiveMonthly * 12)) * 100)
      : null;

  return {
    ...plan,
    limits,
    perks,
    limitLines,
    currency,
    effectiveMonthly,
    effectiveYearly,
    priceDisplay: formatPrice(effectiveMonthly, currency),
    priceDisplayYearly: formatPrice(effectiveYearly, currency),
    yearlyMonthlyEquivalent,
    yearlyMonthlyEquivalentDisplay:
      yearlyMonthlyEquivalent != null ? formatPrice(yearlyMonthlyEquivalent, currency) : null,
    yearlySavingsPercent,
    // A single bullet list for the surfaces that show one (landing page, the
    // signup payment summary): enforced limits first, then the perks.
    features: [
      ...limitLines.map((l) => `${l.label}: ${l.value}`),
      ...perks,
    ],
  };
}

export const PLANS = FALLBACK.map(normalizePlan);
